import { useState } from 'react';
import { admin, metricCard as mc, palette, transition, sparkline as sp } from '../../styles/tokens';

interface MetricCardProps {
  label: string;
  value: string;
  valueColor?: string;
  delta?: string;
  deltaType?: 'up' | 'down' | 'warn' | 'neutral';
  sub?: string;
  sparkData?: number[];
  onClick?: () => void;
  // Selected state (e.g. the active aging bucket on Outstanding). Accent ring.
  active?: boolean;
}

function SparklineBar({ data }: { data: number[] }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: sp.barGap, height: sp.height }}>
      {data.map((v, i) => {
        const pct = v / max;
        const opacity = i === data.length - 1 ? sp.opacityCurrent
          : i >= data.length - 3 ? sp.opacityRecent
          : sp.opacityBase;
        return (
          <div key={i} style={{
            width: sp.barWidth, borderRadius: sp.barRadius,
            height: Math.max(3, pct * sp.height),
            background: sp.barColor, opacity,
          }} />
        );
      })}
    </div>
  );
}

export function MetricCard({ label, value, valueColor, delta, deltaType, sub, sparkData, onClick, active }: MetricCardProps) {
  const [hovered, setHovered] = useState(false);

  const deltaColor = deltaType === 'up' ? mc.trendUpColor
    : deltaType === 'down' ? mc.trendDownColor
    : deltaType === 'warn' ? '#D97706'
    : admin.caption;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        flex: 1, minWidth: 0, padding: '16px 18px',
        background: mc.bg, borderRadius: admin.cardRadius,
        border: `0.5px solid ${active ? palette.accent : hovered ? mc.borderHover : mc.border}`,
        boxShadow: active ? `0 0 0 1px ${palette.accent}` : undefined,
        transition: `border-color ${transition.fast}, box-shadow ${transition.fast}`,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: mc.labelSize, fontWeight: 450, color: admin.caption }}>{label}</span>
        {sparkData && <SparklineBar data={sparkData} />}
      </div>
      <div style={{
        fontSize: mc.valueSize, fontWeight: mc.valueFontWeight,
        color: valueColor ?? admin.header,
        letterSpacing: mc.valueLetterSpacing, lineHeight: 1,
        marginTop: 8,
      }}>
        {value}
      </div>
      {(delta || sub) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          {delta && (
            <span style={{ fontSize: 11, fontWeight: 600, color: deltaColor }}>
              {deltaType === 'up' ? '\u2191' : deltaType === 'down' ? '\u2193' : '\u2022'} {delta}
            </span>
          )}
          {sub && <span style={{ fontSize: 11, color: admin.caption }}>{delta ? '· ' : ''}{sub}</span>}
        </div>
      )}
    </div>
  );
}
